import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Map as MapIcon,
  Grid3x3,
  RefreshCw,
  Search,
  Clock,
  X,
  Loader2,
  ZoomIn,
  ZoomOut,
  ArrowRightLeft,
  Merge,
  Scissors,
  Printer,
  CreditCard,
  Plus,
  Bell,
  AlertTriangle,
  ChefHat,
  CircleDollarSign,
  CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { printBillReceipt } from "@/lib/billPrint";
import { formatSessionPreview } from "@/lib/tableSession";
import TableOpsCard from "@/components/tables/TableOpsCard";
import {
  OPS_STATUS,
  resolveOpsStatus,
  computeFloorKpis,
  computeAreaCounts,
  computeStatusCounts,
  buildFloorAlerts,
  statusFilterMatches,
  STATUS_FILTER_OPTIONS,
} from "@/lib/tableFloorUi";
import { aed } from "@/lib/format";
import { useCurrency } from "@/hooks/useCurrency";

export default function TableManagement() {
  const { code: currencyCode, formatPlain, step: moneyStep } = useCurrency();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;
  const counterId = user?.default_counter?.id;
  const [view, setView] = useState("floor");
  const [floorZoom, setFloorZoom] = useState(1);
  const [area, setArea] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState("");
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitLineIds, setSplitLineIds] = useState([]);
  const [splitTargetId, setSplitTargetId] = useState("");
  const [splitBill, setSplitBill] = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleBill, setSettleBill] = useState(null);
  const [settleMethod, setSettleMethod] = useState("cash");
  const [tenderedAmount, setTenderedAmount] = useState("");
  const [settleLoading, setSettleLoading] = useState(false);
  const [sidebarTab, setSidebarTab] = useState("summary");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["table-floor", branchId, search.trim()],
    queryFn: async () => {
      const params = { branch_id: branchId };
      if (search.trim()) params.search = search.trim();
      const { data: res } = await api.get("/tables/floor", { params });
      return res;
    },
    enabled: Boolean(branchId),
    refetchInterval: 30000,
  });

  const allTables = data?.tables ?? [];
  const areas = data?.areas ?? [];

  const tables = useMemo(
    () => allTables.filter((t) => area === "All" || t.area === area),
    [allTables, area],
  );

  const filtered = useMemo(
    () => tables.filter((t) => statusFilterMatches(t, statusFilter)),
    [tables, statusFilter],
  );

  const floorKpis = useMemo(() => computeFloorKpis(allTables), [allTables]);

  const areaCountSource = useMemo(
    () =>
      statusFilter === "All"
        ? allTables
        : allTables.filter((t) => statusFilterMatches(t, statusFilter)),
    [allTables, statusFilter],
  );
  const areaCounts = useMemo(
    () => computeAreaCounts(areaCountSource, areas),
    [areaCountSource, areas],
  );
  const floorAlerts = useMemo(() => buildFloorAlerts(allTables), [allTables]);

  const statusCounts = useMemo(() => computeStatusCounts(tables), [tables]);

  const selectedTable = useMemo(
    () => tables.find((t) => t.id === selectedId) ?? null,
    [tables, selectedId],
  );

  const selectedBill = useMemo(() => {
    if (!selectedTable?.active_bills?.length) return null;
    return (
      selectedTable.active_bills.find((b) => b.id === selectedBillId)
      || selectedTable.active_bills[0]
    );
  }, [selectedTable, selectedBillId]);

  useEffect(() => {
    if (selectedTable?.active_bills?.[0]) {
      setSelectedBillId(selectedTable.active_bills[0].id);
    } else {
      setSelectedBillId(null);
    }
  }, [selectedTable?.id, selectedTable?.active_bills?.[0]?.id]);

  useEffect(() => {
    if (selectedId) {
      setSidebarTab("table");
    }
  }, [selectedId]);

  useEffect(() => {
    if (sidebarTab === "table" && !selectedId) {
      setSidebarTab("summary");
    }
  }, [sidebarTab, selectedId]);

  const getTableBill = useCallback(
    (table, billId = selectedBillId) =>
      table?.active_bills?.find((b) => b.id === billId) || table?.active_bills?.[0] || null,
    [selectedBillId],
  );

  const invalidateFloor = () => {
    queryClient.invalidateQueries({ queryKey: ["table-floor", branchId] });
  };

  const transferMutation = useMutation({
    mutationFn: async ({ billId, targetTableId }) => {
      const { data } = await api.post(
        "/tables/transfer",
        { bill_id: billId, target_table_id: targetTableId },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (res) => {
      invalidateFloor();
      setTransferOpen(false);
      setTransferTargetId("");
      toast.success(res.message || "Order transferred");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Transfer failed"),
  });

  const mergeMutation = useMutation({
    mutationFn: async ({ sourceBillId, targetBillId }) => {
      const { data } = await api.post(
        "/tables/merge",
        { source_bill_id: sourceBillId, target_bill_id: targetBillId },
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (res) => {
      invalidateFloor();
      setMergeSource(null);
      toast.success(res.message || "Orders merged");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Merge failed"),
  });

  const printMutation = useMutation({
    mutationFn: async (billId) => {
      const { data } = await api.post(
        `/bills/${billId}/print`,
        {},
        { params: { branch_id: branchId } },
      );
      return data;
    },
    onSuccess: (res) => {
      if (printBillReceipt(res.print_receipt)) {
        toast.success(res.message || "Bill printed");
      } else {
        toast.success(res.message || "Bill sent to printer");
      }
      invalidateFloor();
    },
    onError: (err) =>
      toast.error(
        err.response?.data?.message
          || err.response?.data?.errors?.bill?.[0]
          || "Print failed",
      ),
  });

  const settleMutation = useMutation({
    mutationFn: async ({ billId, method, tendered, recallIfHeld }) => {
      let payBillId = billId;
      if (recallIfHeld) {
        const { data: recalled } = await api.post(
          `/bills/${billId}/recall`,
          {},
          { params: { branch_id: branchId } },
        );
        payBillId = recalled.bill.id;
      }
      const base = `/bills/${payBillId}/payments`;
      if (method === "cash") {
        const { data } = await api.post(
          `${base}/cash`,
          { tendered_amount: tendered },
          { params: { branch_id: branchId } },
        );
        return data;
      }
      const { data } = await api.post(`${base}/card`, {}, { params: { branch_id: branchId } });
      return data;
    },
    onSuccess: (res) => {
      const printed = res.print_receipt && printBillReceipt(res.print_receipt);
      const change = Number(res.change_amount || 0);
      const drawerNote = res.drawer_kick ? " · drawer opened" : "";
      if (change > 0) {
        toast.success(
          `Settled — change ${formatPlain(change)}${printed ? " · receipt printed" : ""}${drawerNote}`,
        );
      } else {
        toast.success(
          `${res.message || "Payment completed"}${printed ? " · receipt printed" : ""}${drawerNote}`,
        );
      }
      invalidateFloor();
      setSettleOpen(false);
      setSettleBill(null);
      setTenderedAmount("");
    },
    onError: (err) =>
      toast.error(
        err.response?.data?.message
          || err.response?.data?.errors?.tendered_amount?.[0]
          || err.response?.data?.errors?.bill?.[0]
          || "Settlement failed",
      ),
  });

  const addOrderMutation = useMutation({
    mutationFn: async (tableId) => {
      const { data } = await api.post(
        `/tables/${tableId}/orders`,
        {},
        { params: { branch_id: branchId, counter_id: counterId } },
      );
      return data;
    },
    onSuccess: (res, tableId) => {
      invalidateFloor();
      const table = tables.find((t) => t.id === tableId);
      navigate("/pos", {
        state: {
          openBillId: res.bill.id,
          tableCode: table?.code,
        },
      });
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Failed to add order"),
  });

  const splitMutation = useMutation({
    mutationFn: async ({ billId, lineIds, targetTableId }) => {
      const { data } = await api.post(
        "/tables/split",
        {
          bill_id: billId,
          line_ids: lineIds,
          target_table_id: targetTableId || undefined,
        },
        { params: { branch_id: branchId, counter_id: counterId } },
      );
      return data;
    },
    onSuccess: (res) => {
      invalidateFloor();
      setSplitOpen(false);
      setSplitLineIds([]);
      setSplitTargetId("");
      setSplitBill(null);
      toast.success(res.message || "Bill split");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Split failed"),
  });

  const handleSelectTable = useCallback(
    (tableId) => {
      const table = tables.find((t) => t.id === tableId);
      if (!table) return;

      if (mergeSource) {
        if (table.id === mergeSource.tableId) {
          toast.error("Select a different table to merge into");
          return;
        }
        const targetBill = table.active_bills?.[0];
        if (!targetBill) {
          toast.error("Target table must have an active order");
          return;
        }
        mergeMutation.mutate({
          sourceBillId: mergeSource.billId,
          targetBillId: targetBill.id,
        });
        return;
      }

      setSelectedId(tableId);
      setSidebarTab("table");
    },
    [tables, mergeSource, mergeMutation],
  );

  const handleAlertClick = useCallback((tableId) => {
    setSelectedId(tableId);
    setSidebarTab("table");
  }, []);

  const handleCloseInspector = useCallback(() => {
    setSelectedId(null);
    setSidebarTab("summary");
  }, []);

  const handlePrintBill = useCallback(
    (table) => {
      const bill = getTableBill(table);
      if (!bill) {
        toast.error("No active order to print");
        return;
      }
      if ((bill.line_count ?? 0) === 0) {
        toast.error("Add items before printing");
        return;
      }
      printMutation.mutate(bill.id);
    },
    [getTableBill, printMutation],
  );

  const openSettleDialog = useCallback(
    async (table) => {
      const bill = getTableBill(table);
      if (!bill) {
        toast.error("No active order to settle");
        return;
      }
      if (!counterId) {
        toast.error("Select a counter before settling");
        return;
      }
      if ((bill.line_count ?? 0) === 0) {
        toast.error("Add items before settling");
        return;
      }
      setSettleMethod("cash");
      setSettleOpen(true);
      setSettleLoading(true);
      try {
        const { data } = await api.get(`/bills/${bill.id}`, { params: { branch_id: branchId } });
        setSettleBill(data.bill);
        setTenderedAmount(formatPlain(data.bill.net_amount || 0));
      } catch {
        toast.error("Failed to load bill");
        setSettleOpen(false);
      } finally {
        setSettleLoading(false);
      }
    },
    [branchId, counterId, getTableBill],
  );

  const openTransferDialog = useCallback((table) => {
    const bill = getTableBill(table);
    if (!bill) {
      toast.error("No active order to transfer");
      return;
    }
    setTransferTargetId("");
    setTransferOpen(true);
  }, [getTableBill]);

  const openMergeMode = useCallback((table) => {
    const bill = getTableBill(table);
    if (!bill) {
      toast.error("No active order to merge");
      return;
    }
    setMergeSource({ tableId: table.id, billId: bill.id, tableName: table.name });
    toast.info(`Tap another table to merge ${table.name} into`);
  }, [getTableBill]);

  const openSplitDialog = useCallback(async (table) => {
    const bill = getTableBill(table);
    if (!bill) {
      toast.error("No active order to split");
      return;
    }
    if (!counterId) {
      toast.error("Select a counter before splitting");
      return;
    }
    setSplitLineIds([]);
    setSplitTargetId(String(table.id));
    setSplitOpen(true);
    setSplitLoading(true);
    try {
      const { data } = await api.get(`/bills/${bill.id}`, { params: { branch_id: branchId } });
      setSplitBill(data.bill);
    } catch {
      toast.error("Failed to load bill lines");
      setSplitOpen(false);
    } finally {
      setSplitLoading(false);
    }
  }, [branchId, counterId, getTableBill]);

  const addTableOrder = useCallback(
    (table) => {
      if (!counterId) {
        toast.error("Select a counter before adding an order");
        return;
      }
      addOrderMutation.mutate(table.id);
    },
    [counterId, addOrderMutation],
  );

  const openInPos = useCallback(
    (table) => {
      if (!counterId) {
        toast.error("Select a counter before opening POS");
        return;
      }

      if (table.status === "Vacant") {
        navigate("/pos", {
          state: {
            openTable: {
              restaurant_table_id: table.id,
              table_code: table.code,
              table_name: table.name,
            },
          },
        });
        return;
      }

      const bill = getTableBill(table);
      if (!bill) {
        toast.error("No active order found for this table");
        return;
      }

      navigate("/pos", {
        state: {
          openBillId: bill.id,
          recallIfHeld: bill.status === "held",
          tableCode: table.code,
        },
      });
    },
    [counterId, navigate, getTableBill],
  );

  if (!branchId) {
    return (
      <div className="h-full grid place-items-center text-slate-500 text-sm">
        Select a branch to view tables.
      </div>
    );
  }

  const vacantTables = tables.filter((t) => t.status === "Vacant");
  const transferSourceBill = selectedBill;
  const settleDue = Number(settleBill?.net_amount || 0);
  const splitLines = (splitBill?.lines ?? []).filter((l) => !l.is_combo_component);

  return (
    <div
      className="h-full flex flex-col bg-[#FDF8F5] overflow-hidden tf-floor-page"
      data-testid="tables-screen"
    >
      {mergeSource && (
        <div className="flex-shrink-0 bg-blue-600 text-white px-4 py-2 flex items-center justify-between text-[13px]">
          <span className="flex items-center gap-2">
            <Merge size={16} className="opacity-90" />
            Merging <strong>{mergeSource.tableName}</strong> — tap another table with an active order
          </span>
          <button
            type="button"
            onClick={() => setMergeSource(null)}
            className="text-[12px] font-semibold px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 transition-colors"
            data-testid="merge-cancel"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="flex-shrink-0 border-b border-[#F0E0D4] bg-white px-4 py-3 shadow-sm space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-brand-orange grid place-items-center">
              <MapIcon size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-bold text-brand-brown/45 uppercase tracking-widest">Table Floor</div>
              <div className="text-[15px] font-extrabold text-brand-brown leading-tight">Live operations</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-[#FDF8F5] border border-[#F0E0D4] rounded-xl h-9 px-2.5 w-[200px] lg:w-[260px]">
              <Search size={14} className="text-brand-brown/35 mr-1.5 shrink-0" />
              <Input
                data-testid="tables-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search table, guest, order…"
                className="border-0 shadow-none focus-visible:ring-0 h-7 px-0 bg-transparent text-[13px]"
              />
            </div>
            <div className="flex items-center h-9 rounded-xl border border-[#F0E0D4] bg-[#FDF8F5] p-0.5">
              <ViewToggle
                label="Monitor"
                icon={<Grid3x3 size={12} />}
                active={view === "grid"}
                onClick={() => setView("grid")}
                testid="view-grid"
              />
              <ViewToggle
                label="Layout"
                icon={<MapIcon size={12} />}
                active={view === "floor"}
                onClick={() => setView("floor")}
                testid="view-floor"
              />
            </div>
            <button
              data-testid="tables-refresh"
              onClick={() => refetch()}
              disabled={isFetching}
              className="h-9 w-9 grid place-items-center rounded-xl border border-[#F0E0D4] bg-white text-brand-brown/60 hover:bg-[#FFF8F3] disabled:opacity-50"
            >
              {isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
          </div>
        </div>

        <div className="tf-kpi-bar">
          <div className="tf-kpi-stat">
            <div className="tf-kpi-stat-label">Available</div>
            <div className="tf-kpi-stat-value text-emerald-600">{floorKpis.vacant}</div>
          </div>
          <div className="tf-kpi-stat">
            <div className="tf-kpi-stat-label">Occupied</div>
            <div className="tf-kpi-stat-value text-blue-600">{floorKpis.occupied}</div>
          </div>
          <div className="tf-kpi-stat">
            <div className="tf-kpi-stat-label">Open Orders</div>
            <div className="tf-kpi-stat-value">{floorKpis.openOrders}</div>
          </div>
          <div className="tf-kpi-stat">
            <div className="tf-kpi-stat-label">Revenue</div>
            <div className="tf-kpi-stat-value text-[1.1rem]">{aed(floorKpis.revenue)}</div>
          </div>
          <div className="tf-kpi-stat">
            <div className="tf-kpi-stat-label">Avg Table</div>
            <div className="tf-kpi-stat-value text-[1.1rem]">{aed(floorKpis.avgTableValue)}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setArea("All")}
            className={cn("tf-area-pill", area === "All" && "tf-area-pill--active")}
            data-testid="area-All"
          >
            All
            <span className="tf-area-pill-count">{areaCounts.All ?? 0}</span>
          </button>
          {areas.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setArea(a)}
              className={cn("tf-area-pill", area === a && "tf-area-pill--active")}
              data-testid={`area-${a}`}
            >
              {a}
              <span className="tf-area-pill-count">{areaCounts[a] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_FILTER_OPTIONS.map(({ key, label }) => {
            const ops = OPS_STATUS[key];
            return (
              <button
                key={key}
                type="button"
                data-testid={key === "All" ? "status-all" : `status-${key}`}
                onClick={() => setStatusFilter(key)}
                className={cn(
                  "tf-status-chip border",
                  statusFilter === key
                    ? "bg-brand-brown text-white border-brand-brown"
                    : "bg-white text-brand-brown/70 border-[#F0E0D4] hover:border-brand-orange/40",
                )}
              >
                {ops?.dot && <span className={cn("h-2 w-2 rounded-full", ops.dot)} />}
                {label}
                <span className="opacity-80 font-mono text-[10px]">{statusCounts[key] ?? 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      <AlertsStrip alerts={floorAlerts} onAlertClick={handleAlertClick} />

      <div className="flex-1 flex min-h-0">
        <section className="flex-1 min-w-0 min-h-0 overflow-auto p-3">
          {isLoading ? (
            <div className="h-full grid place-items-center text-slate-500 gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
              <span className="text-[13px]">Loading floor plan…</span>
            </div>
          ) : view === "floor" ? (
            <FloorView
              tables={filtered}
              area={area}
              selectedId={selectedId}
              onSelect={handleSelectTable}
              zoom={floorZoom}
              onZoomChange={setFloorZoom}
              refreshedAt={data?.refreshed_at}
              mergeSourceId={mergeSource?.tableId}
            />
          ) : (
            <GridView
              tables={filtered}
              selectedId={selectedId}
              onSelect={handleSelectTable}
              mergeSourceId={mergeSource?.tableId}
            />
          )}
        </section>

        <div className="hidden lg:flex h-full min-h-0 flex-shrink-0">
          <aside className="hidden xl:flex tf-sidebar-rail" data-testid="floor-alerts-rail">
            <AlertsPanel alerts={floorAlerts} onAlertClick={handleAlertClick} />
          </aside>
          <aside className="tf-sidebar-detail w-[22rem] xl:w-[20rem]" data-testid="floor-detail-panel">
            <FloorDetailPanel
              tab={sidebarTab}
              onTabChange={setSidebarTab}
              alerts={floorAlerts}
              onAlertClick={handleAlertClick}
              alertsCount={floorAlerts.length}
              kpis={floorKpis}
              selectedTable={selectedTable}
              selectedBill={selectedBill}
              onBillSelect={setSelectedBillId}
              onCloseInspector={handleCloseInspector}
              onOpenPos={openInPos}
              onTransfer={() => openTransferDialog(selectedTable)}
              onMerge={() => openMergeMode(selectedTable)}
              onSplit={() => openSplitDialog(selectedTable)}
              onPrint={() => handlePrintBill(selectedTable)}
              onSettle={() => openSettleDialog(selectedTable)}
              onAddOrder={() => addTableOrder(selectedTable)}
              addOrderPending={addOrderMutation.isPending}
              printPending={printMutation.isPending}
            />
          </aside>
        </div>
      </div>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Transfer order</DialogTitle>
          </DialogHeader>
          <p className="text-[12.5px] text-slate-600">
            Move <strong>{selectedTable?.name}</strong> order to a vacant table.
          </p>
          <div>
            <Label className="text-[12px]">Target table</Label>
            <Select value={transferTargetId} onValueChange={setTransferTargetId}>
              <SelectTrigger className="h-9 mt-1" data-testid="transfer-target">
                <SelectValue placeholder="Select vacant table" />
              </SelectTrigger>
              <SelectContent>
                {vacantTables.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.code} — {t.area}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button
              className="bg-[#16A34A] hover:bg-emerald-700"
              disabled={!transferTargetId || transferMutation.isPending}
              data-testid="transfer-confirm"
              onClick={() =>
                transferMutation.mutate({
                  billId: transferSourceBill.id,
                  targetTableId: Number(transferTargetId),
                })
              }
            >
              {transferMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : "Transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Split order</DialogTitle>
          </DialogHeader>
          <p className="text-[12.5px] text-slate-600">
            Move selected items from <strong>{selectedTable?.name}</strong> to a new bill.
          </p>
          {splitLoading ? (
            <div className="h-24 grid place-items-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
                {splitLines.map((line) => (
                  <label
                    key={line.id}
                    className="flex items-center gap-2 px-2.5 py-2 text-[12.5px] cursor-pointer hover:bg-slate-50"
                  >
                    <Checkbox
                      checked={splitLineIds.includes(line.id)}
                      onCheckedChange={(checked) => {
                        setSplitLineIds((prev) =>
                          checked ? [...prev, line.id] : prev.filter((id) => id !== line.id),
                        );
                      }}
                    />
                    <span className="flex-1 font-medium">{line.name}</span>
                    <span className="font-mono text-slate-500">×{line.qty}</span>
                  </label>
                ))}
              </div>
              <div>
                <Label className="text-[12px]">New bill table</Label>
                <Select value={splitTargetId} onValueChange={setSplitTargetId}>
                  <SelectTrigger className="h-9 mt-1" data-testid="split-target">
                    <SelectValue placeholder="Select table" />
                  </SelectTrigger>
                  <SelectContent>
                    {tables.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.code} — {t.area} {t.status === "Vacant" ? "(vacant)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitOpen(false)}>Cancel</Button>
            <Button
              className="bg-[#16A34A] hover:bg-emerald-700"
              disabled={
                splitLoading
                || splitLineIds.length === 0
                || !splitTargetId
                || splitMutation.isPending
              }
              data-testid="split-confirm"
              onClick={() => {
                if (!splitBill?.id) return;
                splitMutation.mutate({
                  billId: splitBill.id,
                  lineIds: splitLineIds,
                  targetTableId: Number(splitTargetId),
                });
              }}
            >
              {splitMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : "Split"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Settle bill</DialogTitle>
          </DialogHeader>
          {settleLoading ? (
            <div className="h-24 grid place-items-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : settleBill ? (
            <>
              <div className="bg-slate-900 text-white rounded-sm px-3 py-2 flex items-baseline justify-between">
                <div>
                  <div className="text-[9.5px] uppercase tracking-wider text-amber-400 font-bold">Due</div>
                  <div className="text-[11px] font-mono text-slate-300">{settleBill.bill_number}</div>
                </div>
                <span className="font-mono font-black text-[22px] tabular-nums">
                  {formatPlain(settleDue)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="settle-cash"
                  onClick={() => setSettleMethod("cash")}
                  className={cn(
                    "h-10 rounded-sm border-2 text-[11px] font-bold flex flex-col items-center justify-center gap-0.5",
                    settleMethod === "cash"
                      ? "border-emerald-700 bg-emerald-500 text-white"
                      : "border-slate-300 bg-white text-slate-800",
                  )}
                >
                  <CircleDollarSign size={14} />
                  CASH
                </button>
                <button
                  type="button"
                  data-testid="settle-card"
                  onClick={() => setSettleMethod("card")}
                  className={cn(
                    "h-10 rounded-sm border-2 text-[11px] font-bold flex flex-col items-center justify-center gap-0.5",
                    settleMethod === "card"
                      ? "border-blue-600 bg-blue-100 text-slate-900"
                      : "border-slate-300 bg-white text-slate-800",
                  )}
                >
                  <CreditCard size={14} />
                  CARD
                </button>
              </div>
              {settleMethod === "cash" && (
                <div>
                  <Label className="text-[12px]">Tendered amount</Label>
                  <Input
                    type="number"
                    min="0"
                    step={moneyStep}
                    value={tenderedAmount}
                    onChange={(e) => setTenderedAmount(e.target.value)}
                    className="h-9 mt-1 font-mono"
                    data-testid="settle-tendered"
                  />
                  {Number(tenderedAmount) > settleDue && (
                    <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                      Change: {formatPlain(Number(tenderedAmount) - settleDue)}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleOpen(false)}>Cancel</Button>
            <Button
              className="bg-[#16A34A] hover:bg-emerald-700"
              disabled={settleLoading || !settleBill || settleMutation.isPending}
              data-testid="settle-confirm"
              onClick={() => {
                if (!settleBill) return;
                const tendered = settleMethod === "cash"
                  ? Number(tenderedAmount || settleDue)
                  : settleDue;
                if (settleMethod === "cash" && tendered < settleDue) {
                  toast.error("Tendered amount is less than bill total");
                  return;
                }
                settleMutation.mutate({
                  billId: settleBill.id,
                  method: settleMethod,
                  tendered,
                  recallIfHeld: settleBill.status === "held",
                });
              }}
            >
              {settleMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : "Settle & Pay"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function layoutSort(a, b) {
  const areaCmp = (a.area || "").localeCompare(b.area || "");
  if (areaCmp !== 0) return areaCmp;
  const yCmp = (Number(a.y) || 0) - (Number(b.y) || 0);
  if (yCmp !== 0) return yCmp;
  const xCmp = (Number(a.x) || 0) - (Number(b.x) || 0);
  if (xCmp !== 0) return xCmp;
  return (a.code || "").localeCompare(b.code || "");
}

function tableLayoutRect(t) {
  return {
    x: Number(t.x) || 0,
    y: Number(t.y) || 0,
    w: Number(t.w) || 90,
    h: Number(t.h) || 90,
  };
}

function FloorView({ tables, area, selectedId, onSelect, zoom, onZoomChange, refreshedAt, mergeSourceId }) {
  const groups = useMemo(() => {
    if (area !== "All") return [{ area, tables }];
    const m = new Map();
    tables.forEach((t) => {
      if (!m.has(t.area)) m.set(t.area, []);
      m.get(t.area).push(t);
    });
    return Array.from(m.entries()).map(([a, ts]) => ({ area: a, tables: ts }));
  }, [tables, area]);

  const zoomIn = () => onZoomChange((z) => Math.min(1.6, Math.round((z + 0.1) * 10) / 10));
  const zoomOut = () => onZoomChange((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10));
  const resetZoom = () => onZoomChange(1);

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="h-9 px-3 flex items-center gap-2 border-b border-[#E5E7EB] bg-white flex-shrink-0">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Floor layout</span>
        <div className="flex items-center gap-0.5 ml-1">
          <button
            type="button"
            onClick={zoomOut}
            className="h-6 w-6 grid place-items-center rounded border border-slate-300 hover:bg-slate-100 text-slate-600"
            aria-label="Zoom out"
            data-testid="floor-zoom-out"
          >
            <ZoomOut size={12} />
          </button>
          <span className="text-[10px] font-mono w-10 text-center text-slate-600">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={zoomIn}
            className="h-6 w-6 grid place-items-center rounded border border-slate-300 hover:bg-slate-100 text-slate-600"
            aria-label="Zoom in"
            data-testid="floor-zoom-in"
          >
            <ZoomIn size={12} />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            className="h-6 px-2 rounded border border-slate-300 text-[10px] font-bold hover:bg-slate-100 text-slate-600"
            data-testid="floor-zoom-fit"
          >
            FIT
          </button>
        </div>
        {refreshedAt && (
          <span className="text-[10px] text-slate-500">
            Updated {new Date(refreshedAt).toLocaleTimeString()}
          </span>
        )}
        <Link
          to="/masters/table-designer"
          className="ml-auto text-[10px] font-bold text-emerald-700 hover:underline"
        >
          Edit layout
        </Link>
      </div>

      <div className="flex-1 overflow-auto p-3 space-y-3 min-h-0">
        {groups.map((g) => (
          <AreaFloorMap
            key={g.area}
            group={g}
            selectedId={selectedId}
            onSelect={onSelect}
            zoom={zoom}
            mergeSourceId={mergeSourceId}
          />
        ))}
        {groups.length === 0 && (
          <div className="text-center text-slate-500 py-12 rounded-lg border border-dashed border-slate-300 bg-white">
            <p className="text-[13px]">No tables match current filters</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AreaFloorMap({ group, selectedId, onSelect, zoom, mergeSourceId }) {
  const containerRef = useRef(null);
  const [fitScale, setFitScale] = useState(0.75);
  const PAD = 28;

  const bounds = useMemo(() => {
    const rects = group.tables.map(tableLayoutRect);
    const minX = Math.min(...rects.map((r) => r.x));
    const minY = Math.min(...rects.map((r) => r.y));
    const maxX = Math.max(...rects.map((r) => r.x + r.w));
    const maxY = Math.max(...rects.map((r) => r.y + r.h));
    return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
  }, [group.tables]);

  const recomputeFit = useCallback(() => {
    const el = containerRef.current;
    if (!el || bounds.width <= 0) return;
    const available = Math.max(400, el.clientWidth - 24);
    const scaleX = (available - PAD * 2) / bounds.width;
    const scaleY = (Math.max(200, bounds.height + 40) - PAD * 2) / Math.max(bounds.height, 1);
    setFitScale(Math.min(1, Math.max(0.55, Math.min(scaleX, scaleY))));
  }, [bounds]);

  useEffect(() => {
    recomputeFit();
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => recomputeFit());
    observer.observe(el);
    return () => observer.disconnect();
  }, [recomputeFit]);

  const scale = fitScale * zoom;
  const canvasW = bounds.width * scale + PAD * 2;
  const canvasH = Math.max(160, bounds.height * scale + PAD * 2);
  const activeCount = group.tables.filter((t) => t.status !== "Vacant").length;

  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden shadow-sm">
      <div className="h-7 bg-slate-50 border-b border-[#E5E7EB] px-3 flex items-center justify-between">
        <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{group.area}</span>
        <span className="text-[10.5px] text-slate-500">
          {group.tables.length} tables · {activeCount} active
        </span>
      </div>
      <div ref={containerRef} className="w-full overflow-x-auto">
        <div
          className="relative mx-auto table-floor-canvas"
          style={{ width: canvasW, height: canvasH }}
          data-testid={`floor-area-${group.area}`}
        >
          {group.tables.map((t) => {
            const sel = selectedId === t.id;
            const mergeTarget = mergeSourceId && t.id !== mergeSourceId && t.status !== "Vacant";
            const rect = tableLayoutRect(t);
            const x = (rect.x - bounds.minX) * scale + PAD;
            const y = (rect.y - bounds.minY) * scale + PAD;
            const tw = Math.max(rect.w * scale, 96);
            const th = Math.max(rect.h * scale, 88);
            return (
              <div
                key={t.id}
                className="absolute"
                style={{ left: x, top: y, width: tw, height: th }}
              >
                <TableOpsCard
                  table={t}
                  selected={sel}
                  mergeTarget={mergeTarget}
                  onClick={() => onSelect(t.id)}
                  testId={`floor-table-${t.code}`}
                  compact
                  className="h-full w-full"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function GridView({ tables, selectedId, onSelect, mergeSourceId }) {
  const sorted = useMemo(() => [...tables].sort(layoutSort), [tables]);

  return (
    <div className="h-full min-h-0">
      <p className="text-[11px] text-brand-brown/45 mb-2 px-1">
        Card grid for quick scanning. Switch to <strong>Layout</strong> to see your saved floor plan positions.
      </p>
      <div className="tf-grid-ops">
        {sorted.map((t) => (
          <TableOpsCard
            key={t.id}
            table={t}
            selected={selectedId === t.id}
            mergeTarget={mergeSourceId && t.id !== mergeSourceId && t.status !== "Vacant"}
            onClick={() => onSelect(t.id)}
            testId={`grid-table-${t.code}`}
          />
        ))}
      </div>
      {tables.length === 0 && (
        <div className="text-center text-[13px] text-brand-brown/40 py-16 border border-dashed border-[#F0E0D4] rounded-2xl bg-white mx-1">
          No tables match current filters
        </div>
      )}
    </div>
  );
}

const ALERT_ICONS = {
  ready: ChefHat,
  billing: CircleDollarSign,
  delay: AlertTriangle,
  kitchen: Clock,
};

function FloorDetailPanel({
  tab,
  onTabChange,
  alerts,
  onAlertClick,
  alertsCount,
  kpis,
  selectedTable,
  selectedBill,
  onBillSelect,
  onCloseInspector,
  onOpenPos,
  onTransfer,
  onMerge,
  onSplit,
  onPrint,
  onSettle,
  onAddOrder,
  addOrderPending,
  printPending,
}) {
  const tabs = [
    { id: "alerts", label: "Alerts", badge: alertsCount, hideOnXl: true },
    { id: "summary", label: "Summary" },
    ...(selectedTable ? [{ id: "table", label: selectedTable.name || "Table" }] : []),
  ];

  return (
    <div className="tf-detail-panel">
      <div className="tf-detail-tabs" role="tablist" aria-label="Floor details">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            data-testid={`floor-tab-${item.id}`}
            onClick={() => onTabChange(item.id)}
            className={cn(
              "tf-detail-tab",
              tab === item.id && "tf-detail-tab--active",
              item.hideOnXl && "xl:hidden",
            )}
          >
            <span className="truncate">{item.label}</span>
            {item.badge > 0 && (
              <span className="tf-detail-tab-badge">{item.badge}</span>
            )}
          </button>
        ))}
      </div>

      <div className="tf-detail-pane" role="tabpanel">
        {tab === "alerts" && (
          <AlertsPanel alerts={alerts} onAlertClick={onAlertClick} />
        )}
        {tab === "summary" && (
          <div className="tf-detail-pane-scroll">
            <FloorSummary kpis={kpis} />
            <StatusLegend />
          </div>
        )}
        {tab === "table" && selectedTable && (
          <Inspector
            table={selectedTable}
            selectedBill={selectedBill}
            onBillSelect={onBillSelect}
            onClose={onCloseInspector}
            onOpenPos={onOpenPos}
            onTransfer={onTransfer}
            onMerge={onMerge}
            onSplit={onSplit}
            onPrint={onPrint}
            onSettle={onSettle}
            onAddOrder={onAddOrder}
            addOrderPending={addOrderPending}
            printPending={printPending}
          />
        )}
      </div>
    </div>
  );
}

function AlertsPanel({ alerts, onAlertClick }) {
  return (
    <div className="tf-alerts-panel" data-testid="floor-alerts-panel">
      <div className="tf-alerts-panel-head">
        <div className="flex items-center gap-2">
          <span className="tf-alerts-bell">
            <Bell size={15} />
          </span>
          <div>
            <div className="text-[13px] font-extrabold text-brand-brown">Active alerts</div>
            <div className="text-[11px] text-brand-brown/50">Needs attention now</div>
          </div>
        </div>
        <span className={cn("tf-alerts-count", alerts.length > 0 && "tf-alerts-count--active")}>
          {alerts.length}
        </span>
      </div>
      <div className="tf-alerts-panel-list">
        {alerts.length === 0 ? (
          <div className="tf-alerts-empty">
            <CheckCircle2 size={22} className="text-emerald-500 mb-2" />
            <p className="text-[13px] font-semibold text-brand-brown">All clear</p>
            <p className="text-[11px] text-brand-brown/45 mt-0.5">No tables need immediate action</p>
          </div>
        ) : (
          alerts.map((alert) => {
            const Icon = ALERT_ICONS[alert.type] || Bell;
            return (
              <button
                key={alert.id}
                type="button"
                onClick={() => onAlertClick(alert.tableId)}
                className={cn("tf-alert-card", `tf-alert-card--${alert.type}`)}
              >
                <span className="tf-alert-card-icon">
                  <Icon size={16} />
                </span>
                <span className="tf-alert-card-body">
                  <span className="tf-alert-card-title">{alert.title}</span>
                  <span className="tf-alert-card-msg">{alert.message}</span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function AlertsStrip({ alerts, onAlertClick }) {
  if (alerts.length === 0) return null;

  return (
    <div className="lg:hidden flex-shrink-0 border-b border-[#F0E0D4] bg-[#FFF8F3] px-3 py-2" data-testid="floor-alerts-strip">
      <div className="flex items-center gap-2 mb-2">
        <Bell size={14} className="text-brand-orange" />
        <span className="text-[12px] font-bold text-brand-brown">Active alerts ({alerts.length})</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {alerts.slice(0, 6).map((alert) => (
          <button
            key={alert.id}
            type="button"
            onClick={() => onAlertClick(alert.tableId)}
            className={cn("tf-alert-chip", `tf-alert-chip--${alert.type}`)}
          >
            {alert.message}
          </button>
        ))}
      </div>
    </div>
  );
}

function FloorSummary({ kpis }) {
  return (
    <div className="p-4" data-testid="floor-summary">
      <div className="text-[10px] font-bold uppercase tracking-widest text-brand-brown/40 mb-3">Live floor</div>
      <div className="grid grid-cols-2 gap-2.5 text-[12px]">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5">
          <div className="text-emerald-700/70 text-[10px] font-bold uppercase">Available</div>
          <div className="text-2xl font-extrabold text-emerald-800">{kpis.vacant}</div>
        </div>
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-2.5">
          <div className="text-blue-700/70 text-[10px] font-bold uppercase">Occupied</div>
          <div className="text-2xl font-extrabold text-blue-900">{kpis.occupied}</div>
        </div>
        <div className="rounded-xl bg-[#FFF8F3] border border-[#F0E0D4] px-3 py-2.5">
          <div className="text-brand-brown/50 text-[10px] font-bold uppercase">Open orders</div>
          <div className="text-2xl font-extrabold text-brand-brown">{kpis.openOrders}</div>
        </div>
        <div className="rounded-xl bg-[#FFF8F3] border border-[#F0E0D4] px-3 py-2.5">
          <div className="text-brand-brown/50 text-[10px] font-bold uppercase">Avg table</div>
          <div className="text-lg font-extrabold text-brand-brown">{aed(kpis.avgTableValue)}</div>
        </div>
        <div className="rounded-xl bg-white border border-[#F0E0D4] px-3 py-2.5 col-span-2 shadow-sm">
          <div className="text-brand-brown/50 text-[10px] font-bold uppercase">Open revenue</div>
          <div className="text-2xl font-extrabold text-brand-orange">{aed(kpis.revenue)}</div>
        </div>
      </div>
      <p className="mt-4 text-[12px] text-brand-brown/45 text-center leading-relaxed">
        Tap a table or an alert to view details and actions.
      </p>
    </div>
  );
}

function StatusLegend() {
  return (
    <div className="tf-status-legend">
      <div className="tf-status-legend-title">Status colors</div>
      <div className="tf-status-legend-grid">
        {Object.values(OPS_STATUS).filter((s) => s.key !== "multi").map((s) => (
          <div key={s.key} className="flex items-center gap-1.5 text-[10px] text-brand-brown/70 font-semibold">
            <span className={cn("h-2 w-2 rounded-full shrink-0", s.dot)} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function Inspector({
  table,
  selectedBill,
  onBillSelect,
  onClose,
  onOpenPos,
  onTransfer,
  onMerge,
  onSplit,
  onPrint,
  onSettle,
  onAddOrder,
  addOrderPending,
  printPending,
}) {
  if (!table) return null;
  const ops = resolveOpsStatus(table);
  const pendingOrders = table.pending_orders || table.active_bills || [];
  const primaryBill = selectedBill || pendingOrders[0];
  const multiBills = pendingOrders.length > 1;
  const tableTotal = Number(table.total_net_amount ?? pendingOrders.reduce((sum, b) => sum + Number(b.net_amount || 0), 0));

  return (
    <div className="tf-inspector" data-testid="table-inspector">
      <div className="flex-shrink-0 p-4 border-b border-[#F0E0D4] bg-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-brand-brown/45">{table.area}</div>
            <div className="text-[28px] font-black text-brand-brown leading-tight">{table.name}</div>
          </div>
          <button onClick={onClose} className="text-brand-brown/35 hover:text-brand-brown" data-testid="inspector-close">
            <X size={16} />
          </button>
        </div>
        <div className={cn("mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide", ops.badge)}>
          <span className={cn("h-2 w-2 rounded-full", ops.dot)} />
          {table.dining_phase_label || ops.label}
          {table.elapsed ? ` · ${table.elapsed}` : ""}
        </div>
      </div>

      <div className="tf-detail-pane-scroll">
        {table.status === "Vacant" ? (
          <div className="p-3 text-[12.5px] text-slate-500 leading-relaxed">
            Table is available. Tap to start a new order on <strong>{table.name}</strong>.
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-[#E5E7EB]">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[9.5px] uppercase tracking-[0.15em] font-bold text-slate-500">
                  {multiBills ? "Pending Orders" : "Current Order"}
                </div>
                <button
                  type="button"
                  data-testid="tbl-add-order"
                  onClick={onAddOrder}
                  disabled={addOrderPending}
                  className="h-6 px-2 rounded-sm border border-blue-300 bg-blue-50 text-blue-800 text-[10px] font-bold hover:bg-blue-100 disabled:opacity-60 flex items-center gap-1"
                >
                  {addOrderPending ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
                  ADD ORDER
                </button>
              </div>
              {multiBills && (
                <div className="space-y-1 mb-2 max-h-28 overflow-y-auto">
                  {pendingOrders.map((b) => {
                    const active = primaryBill?.id === b.id;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`pending-order-${b.table_order_no || b.id}`}
                        onClick={() => onBillSelect(b.id)}
                        className={cn(
                          "w-full text-left px-2 py-1.5 rounded-sm border text-[11px] flex items-center justify-between",
                          active
                            ? "border-blue-400 bg-blue-50 text-blue-900"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                        )}
                      >
                        <span className="font-mono font-bold">
                          #{b.table_order_no || "—"} {b.bill_number}
                        </span>
                        <span className="font-mono">{formatPlain(b.net_amount || 0)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 text-[12.5px]">
                <Row label="Guest" value={primaryBill?.customer_name || table.customer || "Walk-in"} />
                <Row label="Waiter" value={table.waiter_name || primaryBill?.waiter_name || "—"} />
                <Row label="Covers" value={table.reservation?.party_size || table.capacity || "—"} mono />
                <Row label="Order #" value={primaryBill?.bill_number || table.order} mono />
                <Row label="Phase" value={primaryBill?.dining_phase_label || table.dining_phase_label || "—"} cap />
                <Row label="Seated" value={primaryBill?.elapsed || table.elapsed || "—"} mono />
                <Row label="Items" value={primaryBill?.line_count ?? table.line_count ?? 0} mono />
                <Row label="Bills" value={table.active_bill_count ?? 1} mono />
                <Row
                  label="Kitchen"
                  value={
                    (primaryBill?.session?.in_kitchen_qty ?? table.session?.in_kitchen_qty)
                      ? `${primaryBill?.session?.in_kitchen_qty ?? table.session?.in_kitchen_qty} plating`
                      : "—"
                  }
                />
                <Row label="Printed" value={primaryBill?.printed_at ? "Yes" : "No"} />
              </div>
              {formatSessionPreview(primaryBill?.session || table.session) && (
                <div className="mt-2 text-[11px] text-slate-600 leading-snug border-t border-slate-100 pt-2">
                  <span className="font-bold text-slate-500 uppercase text-[9px] tracking-wider">On table</span>
                  <div className="mt-0.5">{formatSessionPreview(primaryBill?.session || table.session)}</div>
                </div>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                  {multiBills ? "Table Total" : "Running Total"}
                </span>
                <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                  <Clock size={10} /> {primaryBill?.elapsed || table.elapsed}
                </span>
              </div>
              <div className="bg-slate-900 text-white rounded-sm px-3 py-2 flex items-baseline justify-between">
                <span className="text-[9.5px] uppercase tracking-[0.15em] font-bold text-amber-400">{currencyCode}</span>
                <span className="font-mono font-black text-[26px] tabular-nums leading-none">
                  {formatPlain(multiBills ? tableTotal : Number(primaryBill?.net_amount ?? table.net_amount ?? 0))}
                </span>
              </div>
              {multiBills && (
                <div className="text-[10px] text-slate-500 mt-1 font-mono">
                  Selected: {formatPlain(primaryBill?.net_amount || 0)}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {table.status !== "Vacant" && (
        <div className="flex-shrink-0 px-2 pt-2 grid grid-cols-3 gap-1 bg-white">
          <ActionBtn testid="tbl-transfer" onClick={onTransfer} icon={<ArrowRightLeft size={11} />} label="TRANSFER" />
          <ActionBtn testid="tbl-merge" onClick={onMerge} icon={<Merge size={11} />} label="MERGE" />
          <ActionBtn testid="tbl-split" onClick={onSplit} icon={<Scissors size={11} />} label="SPLIT" />
        </div>
      )}

      <div className="flex-shrink-0 p-2 border-t border-[#E5E7EB] grid grid-cols-3 gap-1 bg-white">
        <button
          type="button"
          data-testid="tbl-open-pos"
          onClick={() => onOpenPos(table)}
          className="h-9 rounded-sm border border-slate-900 bg-slate-900 text-white text-[10.5px] font-bold hover:bg-slate-800"
        >
          {table.status === "Vacant" ? "NEW ORDER" : "OPEN POS"}
        </button>
        {table.status !== "Vacant" ? (
          <>
            <button
              type="button"
              data-testid="tbl-print"
              onClick={onPrint}
              disabled={printPending}
              className="h-9 rounded-sm border border-slate-400 bg-white text-slate-800 text-[10.5px] font-bold hover:bg-slate-100 disabled:opacity-60 flex items-center justify-center gap-1"
            >
              {printPending ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
              PRINT
            </button>
            <button
              type="button"
              data-testid="tbl-settle"
              onClick={onSettle}
              className="h-9 rounded-sm border border-emerald-700 bg-emerald-600 text-white text-[10.5px] font-bold hover:bg-emerald-700"
            >
              SETTLE
            </button>
          </>
        ) : (
          <div className="col-span-2" />
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono, cap }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 font-semibold">{label}</span>
      <span className={cn("text-slate-900 font-semibold", mono && "font-mono", cap && "capitalize")}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function ActionBtn({ testid, onClick, icon, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="h-8 rounded-sm border border-slate-300 bg-white text-slate-800 text-[10px] font-bold hover:bg-slate-50 flex items-center justify-center gap-1"
    >
      {icon}
      {label}
    </button>
  );
}

function ViewToggle({ label, icon, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "h-7 px-2.5 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors",
        active ? "bg-brand-orange text-white" : "text-brand-brown/70 hover:bg-white",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
